import React, { useState, useEffect, useRef } from 'react';
import { Wallet, Target, Coins, ArrowRight } from 'lucide-react';
import './HowItWorksSection.css';

const HowItWorksSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // 自动切换步骤
  useEffect(() => {
    if (!isVisible) return;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 3000);

    return () => clearInterval(interval);
  }, [isVisible]);

  const steps = [
    {
      number: '01',
      title: 'Connect Wallet',
      description: 'Seamlessly connect your Web3 wallet and join the future of crypto predictions.',
      icon: Wallet,
      gradient: 'from-emerald-400 to-teal-500',
      bgGradient: 'from-emerald-50 to-teal-50',
      color: '#10b981'
    },
    {
      number: '02',
      title: 'Make Predictions',
      description: 'Leverage AI-powered insights to predict price movements across 100+ crypto pairs.',
      icon: Target,
      gradient: 'from-blue-400 to-indigo-500',
      bgGradient: 'from-blue-50 to-indigo-50',
      color: '#3b82f6'
    },
    {
      number: '03',
      title: 'Earn Rewards',
      description: 'Get rewarded with CRICKET tokens for accurate predictions and daily missions.',
      icon: Coins,
      gradient: 'from-amber-400 to-orange-500',
      bgGradient: 'from-amber-50 to-orange-50',
      color: '#f59e0b'
    }
  ];

  return (
    <section ref={sectionRef} className="how-it-works-modern" id="how-it-works">
      {/* 网格覆盖层 */}
      <div className="grid-overlay"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 flex flex-col justify-center min-h-screen">
        {/* 标题部分 */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="text-4xl sm:text-4xl md:text-5xl lg:text-7xl font-extrabold text-gray-800 mb-4 sm:mb-10">
            How It Works
          </div>
          <div className="text-base sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-[1.4] sm:leading-[1.6] font-light">
            Get started with Cricket AI in three simple steps
          </div>
        </div>

        {/* 步骤网格 - 移动端竖向交替排列，桌面端网格布局 */}
        <div className="relative">
          {/* 移动端竖向交替排列 */}
          <div className="md:hidden space-y-6 px-4">
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              
              return (
                <div 
                  key={index} 
                  className={`flex transition-all duration-700 ${
                    index % 2 === 0 
                      ? 'justify-start pr-8' 
                      : index === 1 
                        ? 'justify-end pl-8' 
                        : 'justify-start pr-8'
                  } ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <div className="w-80 max-w-full">
                    <div className={`bg-white rounded-xl p-5 shadow-lg relative transition-all duration-500 ${
                      isActive ? 'shadow-2xl scale-105 -translate-y-2' : 'hover:shadow-xl'
                    }`}>
                      {/* 步骤编号 */}
                      <div className={`absolute -top-3 -left-3 w-8 h-8 rounded-lg bg-gradient-to-r ${step.gradient} flex items-center justify-center text-white font-bold text-sm shadow-lg ${
                        isActive ? 'animate-pulse scale-110' : ''
                      }`}>
                        {step.number}
                      </div>
                      
                      <div className="pt-1">
                        {/* 第一行：标题 + 图标 */}
                        <div className="flex items-center justify-end space-x-3 mb-3">
                          <div className={`text-base font-bold text-gray-900 transition-all duration-300 ${
                            isActive ? 'scale-105' : ''
                          }`}>
                            {step.title}
                          </div>
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${step.gradient} flex items-center justify-center text-white shadow-lg transition-all duration-500 ${
                            isActive ? 'scale-110 rotate-3' : ''
                          }`}>
                            <step.icon size={18} />
                          </div>
                        </div>
                        
                        {/* 第二行：描述文字 */}
                        <div className={`text-xs text-gray-600 leading-relaxed text-right transition-all duration-300 ${
                          isActive ? 'text-gray-800' : ''
                        }`}>
                          {step.description}
                        </div>
                      </div>

                      {/* 激活状态装饰 */}
                      {isActive && (
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 桌面端网格布局 */}
          <div className="hidden md:grid md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = activeStep === index;
              const isPassed = activeStep > index;
              
              return (
                <div
                  key={index}
                  className={`relative transition-all duration-700 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                  onMouseEnter={() => setActiveStep(index)}
                >
                  {/* 连接线 */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 -translate-x-6 z-0">
                      <div className={`h-full transition-all duration-1000 ${
                        isPassed ? `bg-gradient-to-r ${step.gradient}` : 'bg-gray-200'
                      }`}>
                        <div className={`absolute right-0 top-1/2 -translate-y-1/2 transition-all duration-500 ${
                          isPassed ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
                        }`}>
                          <ArrowRight size={16} className="text-gray-400" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 步骤卡片 */}
                  <div className={`relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 ${
                    isActive ? `border-transparent bg-gradient-to-br ${step.bgGradient}` : 'border-gray-100 hover:border-gray-200'
                  } ${isActive ? 'scale-105 -translate-y-2' : 'hover:scale-102'}`}>
                    
                    {/* 步骤编号 */}
                    <div className={`absolute -top-4 -left-4 w-12 h-12 rounded-2xl bg-gradient-to-r ${step.gradient} flex items-center justify-center text-white font-bold text-lg shadow-lg ${
                      isActive ? 'animate-pulse' : ''
                    }`}>
                      {step.number}
                    </div>

                    {/* 图标区域 */}
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${step.gradient} flex items-center justify-center mb-6 mx-auto transition-all duration-500 ${
                      isActive ? 'scale-110 rotate-3' : 'hover:scale-105'
                    }`}>
                      <StepIcon size={32} className="text-white" />
                    </div>

                    {/* 内容区域 */}
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 mb-4">
                        {step.title}
                      </div>
                      <div className="text-gray-600 leading-relaxed">
                        {step.description}
                      </div>
                    </div>

                    {/* 激活状态装饰 */}
                    {isActive && (
                      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA按钮 */}
        <div className={`text-center mt-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`} style={{ transitionDelay: '800ms' }}>
          <button 
            className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
            onClick={() => window.open('https://app.cricket-ai.xyz/', '_blank')}
          >
            <span className="relative z-10 flex items-center gap-2">
              Get Started Now
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
            </span>
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
